import { useI18n } from '@/hooks/useI18n';
import { CustomerInfo } from "./CustomerInfo";
import { DashboardItinerary } from "@/lib/models/dashboard";
import {
    MAIL_LANGUAGE_LABELS,
    type MailLanguage,
} from "@/lib/shared-proposal/hooks/useClientMailLanguage";
import type { ProposalPaymentMethod } from "@/lib/payments/models/payment-options.model";

interface QuoteReviewProps {
    itinerary: DashboardItinerary;
    emailSubject: string;
    customMessage: string;
    logoChoice: 'own' | 'agency' | null;
    includePdf: boolean;
    pdfFilename: string | null;
    sharingMethod: 'create_user' | 'existing_user';
    contactEmail: string;
    sendEmail: boolean;
    mailLanguage: MailLanguage;
    /** Lingua sul profilo del cliente, per segnalare che si sta spedendo in un'altra lingua. */
    clientProfileLanguage: MailLanguage | null;
    /** Metodi di pagamento scelti nello step Pagamento. Assente nel flusso senza step. */
    paymentMethods?: ProposalPaymentMethod[];
}

export const QuoteReview = ({
    itinerary,
    emailSubject,
    customMessage,
    logoChoice,
    includePdf,
    pdfFilename,
    sharingMethod,
    sendEmail,
    contactEmail,
    mailLanguage,
    clientProfileLanguage,
    paymentMethods,
}: QuoteReviewProps) => {
    const { t } = useI18n();

    const getLogoLabel = () => {
        if (logoChoice === 'own') return t('itinerary.ownLogo');
        if (logoChoice === 'agency') return t('itinerary.agencyLogo');
        return <span className="text-gray-400 italic">{t('common.noLogo')}</span>;
    };

    return (
      <div className="space-y-6">
        <div className="text-center pb-2">
          <h3 className="text-xl font-semibold text-gray-900">
            {t("itinerary.reviewSummary") || "Review Summary"}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t("itinerary.reviewBeforeSending") ||
              "Please review the details before sending the quote"}
          </p>
        </div>

        <div className="bg-gray-50 p-6 gap-2 rounded-lg grid grid-cols-1 md:grid-cols-2">
          {/* Sharing Method */}
          <div>
            <dt className="text-sm font-medium text-gray-500">
              {t("itinerary.reviewSharingMethod") || "Sharing Method:"}
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {sharingMethod === "create_user" &&
                (t("itinerary.reviewCreateUser") || "Client account access")}
              {sharingMethod === "existing_user" &&
                (t("itinerary.reviewExistingUser") || "Client account access")}
            </dd>
          </div>

          {/* Send Email Status */}
          <div>
            <dt className="text-sm font-medium text-gray-500">
              {t("itinerary.reviewSendEmail") || "Send Email:"}
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {sendEmail ? (
                <span className="text-green-600 font-medium">
                  {t("common.yes") || "Yes"}
                </span>
              ) : (
                <span className="text-orange-600 font-medium">
                  {t("itinerary.manualSending")}
                </span>
              )}
            </dd>
          </div>

          {paymentMethods && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("paymentOptions.review.title")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {paymentMethods.length > 0
                  ? paymentMethods.map((m) => t(`paymentOptions.methods.${m}`)).join(", ")
                  : <span className="text-gray-400 italic">{t("paymentOptions.review.none")}</span>}
              </dd>
            </div>
          )}

          {/* Lingua della mail - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewMailLanguage")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {MAIL_LANGUAGE_LABELS[mailLanguage]}
                {clientProfileLanguage !== null && mailLanguage !== clientProfileLanguage && (
                  <span className="text-amber-700 ml-1">
                    {t("itinerary.reviewMailLanguageOverridden", {
                      profile: MAIL_LANGUAGE_LABELS[clientProfileLanguage],
                    })}
                  </span>
                )}
              </dd>
            </div>
          )}

          {/* Email Subject - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewEmailSubject") || "Subject:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{emailSubject}</dd>
            </div>
          )}

          {/* Custom Message - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewCustomMessage") || "Message:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 line-clamp-3">
                {customMessage || (
                  <span className="text-gray-400 italic">
                    {t("itinerary.noCustomMessage") || "No custom message"}
                  </span>
                )}
              </dd>
            </div>
          )}

          {/* Logo Choice - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewLogo") || "Logo:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{getLogoLabel()}</dd>
            </div>
          )}

          {/* Include PDF - only if sending email */}
          {sendEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {t("itinerary.reviewPdf") || "PDF:"}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {includePdf
                  ? pdfFilename || t("itinerary.pdfWillBeAttached")
                  : t("itinerary.pdfNotIncluded") || "Not included"}
              </dd>
            </div>
          )}
        </div>

        {/* Senza mail: il cliente trova la proposta solo accedendo */}
        {!sendEmail && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">
                  {t("itinerary.manualSendingTitle")}
                </p>
                <p className="text-blue-700">
                  {t("itinerary.manualSendingInfoReview")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
};
